import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * MobileSheet — bottom-sheet modal used on mobile to host filter
 * controls that would otherwise crowd the viewport. Desktop should
 * render the same controls inline; the parent decides which is
 * visible via responsive utility classes.
 *
 * Renders nothing when `open` is false. Locks body scroll while open.
 * Portals to document.body to escape any ancestor `transform` (which
 * would create a new containing block and break `position: fixed`).
 */
export default function MobileSheet({ open, onClose, title, children, testId }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  const node = (
    <div
      className="fixed inset-0 z-[60] flex items-end md:hidden"
      data-testid={testId}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-h-[85vh] overflow-y-auto bg-[var(--bg-panel)] border-t-2 border-cyan-sys rounded-t-lg"
      >
        <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-[var(--border-default)] bg-[var(--bg-panel)] z-10">
          <div className="font-display uppercase tracking-[0.25em] text-[11px] text-cyan-sys">
            {title}
          </div>
          <button
            onClick={onClose}
            data-testid={`${testId}-close`}
            className="p-1 text-[var(--txt-muted)] hover:text-cyan-sys min-w-[36px] min-h-[36px] inline-flex items-center justify-center"
            aria-label="close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
